import { useState } from "react";
import { useFreelancerContext } from "../contexts/useFreelancerContext";
import {
	usePatchMyAvailabilityFreelancerOnboardingStep,
	usePatchMyExpertiseFreelancerOnboardingStep,
	usePatchMyPersonalInformationFreelancerOnboardingStep,
	usePatchMyPresentationFreelancerOnboardingStep,
	usePatchMyProfessionalInformationFreelancerOnboardingStep,
	usePatchMyProfileFreelancerOnboardingStep,
	usePatchMySectorsFreelancerOnboardingStep,
	usePatchMySkillsFreelancerOnboardingStep,
	useSaveMyExpertiseFreelancerOnboardingProgress,
	useSaveMyPresentationFreelancerOnboardingProgress,
	useSaveMyProfileFreelancerOnboardingProgress,
	useSaveMySectorsFreelancerOnboardingProgress,
	useSaveMySkillsFreelancerOnboardingProgress,
	useSaveMyAvailabilityFreelancerOnboardingProgress,
	useSaveMyProfessionalInformationFreelancerOnboardingProgress,
	useSaveMyPersonalInformationFreelancerOnboardingProgress,
	SaveOnboardingOptions
} from "./useFreelancerProfile";
import { FreelancerOnboardingFormStep } from "../models/enums/FreelancerOnboardingFormStep";
import { useUpdateFreelancerOnboardingFormsProgressCurrentStep } from "./useFreelancerOnboarding";

export const useFreelancerOnboardingFormSave = ({
	hideAllSnackbars = false
}: SaveOnboardingOptions = {}) => {
	const {
		freelancerOnboardingMyExpertiseStepViewModel,
		freelancerOnboardingMySkillsStepViewModel,
		freelancerOnboardingMySectorsStepViewModel,
		freelancerOnboardingMyPresentationStepViewModel,
		freelancerOnboardingMyAvailabilityStepViewModel,
		freelancerOnboardingMyProfessionalInformationStepViewModel,
		freelancerOnboardingMyPersonalInformationStepViewModel,
		freelancerQueryResult,
		freelancerProfileQueryResult,
		newFreelancerProfileProfessionalExperience,
		currentOnboardingFormStep: currentOnboardingStep,
		setCurrentOnboardingFormStep,
		setFreelancerOnboardingMyExpertiseStepViewModel,
		setFreelancerOnboardingMySkillsStepViewModel,
		setFreelancerOnboardingMySectorsStepViewModel,
		setFreelancerOnboardingMyPresentationStepViewModel,
		setFreelancerOnboardingMyAvailabilityStepViewModel,
		setFreelancerOnboardingMyProfessionalInformationStepViewModel,
		setFreelancerOnboardingMyPersonalInformationStepViewModel,
		saveTranslationsAsync
	} = useFreelancerContext();

	const { data: freelancerProfile } = freelancerProfileQueryResult;
	const { data: freelancer } = freelancerQueryResult;

	const [isSaveProgressPending, setIsSaveProgressPending] = useState(false);

	// Save hooks
	const patchMyProfileFreelancerOnboardingStep =
		usePatchMyProfileFreelancerOnboardingStep({ hideAllSnackbars });
	const patchMyExpertiseFreelancerOnboardingStep =
		usePatchMyExpertiseFreelancerOnboardingStep({ hideAllSnackbars });
	const patchMySkillsFreelancerOnboardingStep = usePatchMySkillsFreelancerOnboardingStep({
		hideAllSnackbars
	});
	const patchMySectorsFreelancerOnboardingStep =
		usePatchMySectorsFreelancerOnboardingStep({ hideAllSnackbars });
	const patchMyPresentationFreelancerOnboardingStep =
		usePatchMyPresentationFreelancerOnboardingStep({ hideAllSnackbars });
	const patchMyAvailabilityFreelancerOnboardingStep =
		usePatchMyAvailabilityFreelancerOnboardingStep({ hideAllSnackbars });
	const patchMyProfessionalInformationFreelancerOnboardingStep =
		usePatchMyProfessionalInformationFreelancerOnboardingStep({ hideAllSnackbars });
	const patchMyPersonalInformationFreelancerOnboardingStep =
		usePatchMyPersonalInformationFreelancerOnboardingStep({ hideAllSnackbars });
	// Save progression hooks
	const saveMyProfileFreelancerOnboardingProgression =
		useSaveMyProfileFreelancerOnboardingProgress({ hideAllSnackbars });
	const saveMyExpertiseFreelancerOnboardingProgression =
		useSaveMyExpertiseFreelancerOnboardingProgress({ hideAllSnackbars });
	const saveMySkillsFreelancerOnboardingProgression =
		useSaveMySkillsFreelancerOnboardingProgress({ hideAllSnackbars });
	const saveMySectorsFreelancerOnboardingProgression =
		useSaveMySectorsFreelancerOnboardingProgress({ hideAllSnackbars });
	const saveMyPresentationFreelancerOnboardingProgression =
		useSaveMyPresentationFreelancerOnboardingProgress({ hideAllSnackbars });
	const saveMyAvailabilityFreelancerOnboardingProgression =
		useSaveMyAvailabilityFreelancerOnboardingProgress({ hideAllSnackbars });
	const saveMyProfessionalInformationFreelancerOnboardingProgress =
		useSaveMyProfessionalInformationFreelancerOnboardingProgress({ hideAllSnackbars });
	const saveMyPersonalInformationFreelancerOnboardingProgress =
		useSaveMyPersonalInformationFreelancerOnboardingProgress({ hideAllSnackbars });

	const updateOnboardingFormsStep = useUpdateFreelancerOnboardingFormsProgressCurrentStep(
		{ hideAllSnackbars }
	);

	const validateAndSaveOnboardingFormAsync = async (
		currentStep: FreelancerOnboardingFormStep,
		newStep: FreelancerOnboardingFormStep
	) => {
		setIsSaveProgressPending(true);
		try {
			switch (currentStep) {
				case FreelancerOnboardingFormStep.MyProfile:
					await patchMyProfileFreelancerOnboardingStep.mutateAsync({
						freelancerProfile: freelancerProfile!,
						newStep
					});
					break;
				case FreelancerOnboardingFormStep.MyExpertise:
					await patchMyExpertiseFreelancerOnboardingStep.mutateAsync({
						freelancerOnboardingMyExpertiseStepViewModel:
							freelancerOnboardingMyExpertiseStepViewModel!,
						freelancerProfile: freelancerProfile!,
						newStep
					});
					if (saveTranslationsAsync) {
						await saveTranslationsAsync();
					}
					break;
				case FreelancerOnboardingFormStep.MySkills:
					await patchMySkillsFreelancerOnboardingStep.mutateAsync({
						freelancer: freelancer!,
						freelancerProfile: freelancerProfile!,
						freelancerOnboardingMySkillsStepViewModel:
							freelancerOnboardingMySkillsStepViewModel!,
						newStep
					});
					break;
				case FreelancerOnboardingFormStep.MySectors:
					await patchMySectorsFreelancerOnboardingStep.mutateAsync({
						freelancerProfile: freelancerProfile!,
						freelancerOnboardingMySectorsStepViewModel:
							freelancerOnboardingMySectorsStepViewModel!,
						newStep
					});
					break;
				case FreelancerOnboardingFormStep.MyPresentation:
					await patchMyPresentationFreelancerOnboardingStep.mutateAsync({
						freelancerProfile: freelancerProfile!,
						freelancerOnboardingMyPresentationStepViewModel:
							freelancerOnboardingMyPresentationStepViewModel!,
						newStep
					});
					if (saveTranslationsAsync) {
						await saveTranslationsAsync();
					}
					break;
				case FreelancerOnboardingFormStep.MyAvailability:
					await patchMyAvailabilityFreelancerOnboardingStep.mutateAsync({
						freelancerProfile: freelancerProfile!,
						freelancerOnboardingMyAvailabilityStepViewModel:
							freelancerOnboardingMyAvailabilityStepViewModel!,
						newStep
					});
					break;
				case FreelancerOnboardingFormStep.MyProfessionalInformation:
					await patchMyProfessionalInformationFreelancerOnboardingStep.mutateAsync({
						freelancerOnboardingMyProfessionalInformationStepViewModel:
							freelancerOnboardingMyProfessionalInformationStepViewModel!,
						freelancerProfile: freelancerProfile!,
						newStep
					});
					break;
				case FreelancerOnboardingFormStep.MyPersonalInformation:
					await patchMyPersonalInformationFreelancerOnboardingStep.mutateAsync({
						freelancerOnboardingMyPersonalInformationStepViewModel:
							freelancerOnboardingMyPersonalInformationStepViewModel!,
						freelancer: freelancer!,
						newStep
					});
					break;
			}
			setIsSaveProgressPending(false);
		} catch (error) {
			console.error("Error validating and saving onboarding step:", error);
			setIsSaveProgressPending(false);
			// Rethrow so the caller does NOT treat a rejected save as a successful step change.
			throw error;
		}
	};

	const saveOnboardingFormWithoutValidationAsync = async (
		currentStep: FreelancerOnboardingFormStep,
		newStep: FreelancerOnboardingFormStep
	) => {
		setIsSaveProgressPending(true);

		try {
			switch (currentStep) {
				case FreelancerOnboardingFormStep.MyProfile:
					await saveMyProfileFreelancerOnboardingProgression.mutateAsync({
						freelancerProfile: freelancerProfile!,
						newStep
					});
					break;
				case FreelancerOnboardingFormStep.MyExpertise:
					await saveMyExpertiseFreelancerOnboardingProgression.mutateAsync({
						freelancerOnboardingMyExpertiseStepViewModel:
							freelancerOnboardingMyExpertiseStepViewModel!,
						freelancerProfile: freelancerProfile!,
						newStep
					});

					if (saveTranslationsAsync) {
						await saveTranslationsAsync();
					}

					break;
				case FreelancerOnboardingFormStep.MySkills:
					await saveMySkillsFreelancerOnboardingProgression.mutateAsync({
						freelancer: freelancer!,
						freelancerProfile: freelancerProfile!,
						freelancerOnboardingMySkillsStepViewModel:
							freelancerOnboardingMySkillsStepViewModel!,
						newStep
					});
					break;
				case FreelancerOnboardingFormStep.MySectors:
					await saveMySectorsFreelancerOnboardingProgression.mutateAsync({
						freelancerProfile: freelancerProfile!,
						freelancerOnboardingMySectorsStepViewModel:
							freelancerOnboardingMySectorsStepViewModel!,
						newStep
					});
					break;
				case FreelancerOnboardingFormStep.MyPresentation: {
					// Translation cells FIRST: the step-progress endpoint validates that the
					// presentation exists in at least one language AT CALL TIME (state check).
					// Translation-only patches skip the step validation, so saving them before
					// is what makes a first-visit presentation (typed but not yet persisted)
					// satisfy the check instead of 400ing.
					if (saveTranslationsAsync) {
						await saveTranslationsAsync();
					}

					await saveMyPresentationFreelancerOnboardingProgression.mutateAsync({
						freelancerProfile: freelancerProfile!,
						freelancerOnboardingMyPresentationStepViewModel: {
							...freelancerOnboardingMyPresentationStepViewModel!,
							professionalExperiences:
								freelancerOnboardingMyPresentationStepViewModel!.professionalExperiences.filter(
									professionalExperience =>
										JSON.stringify(professionalExperience) !==
										JSON.stringify(newFreelancerProfileProfessionalExperience)
								)
						},
						newStep
					});

					break;
				}
				case FreelancerOnboardingFormStep.MyAvailability:
					await saveMyAvailabilityFreelancerOnboardingProgression.mutateAsync({
						freelancerProfile: freelancerProfile!,
						freelancerOnboardingMyAvailabilityStepViewModel:
							freelancerOnboardingMyAvailabilityStepViewModel!,
						newStep
					});
					break;
				case FreelancerOnboardingFormStep.MyProfessionalInformation:
					await saveMyProfessionalInformationFreelancerOnboardingProgress.mutateAsync({
						freelancerProfile: freelancerProfile!,
						freelancerOnboardingMyProfessionalInformationStepViewModel:
							freelancerOnboardingMyProfessionalInformationStepViewModel!,
						newStep
					});
					break;
				case FreelancerOnboardingFormStep.MyPersonalInformation:
					await saveMyPersonalInformationFreelancerOnboardingProgress.mutateAsync({
						freelancer: freelancer!,
						freelancerOnboardingMyPersonalInformationStepViewModel:
							freelancerOnboardingMyPersonalInformationStepViewModel!,
						newStep
					});
					break;
			}

			setIsSaveProgressPending(false);
		} catch (error) {
			console.error("Error saving onboarding step:", error);
			setIsSaveProgressPending(false);
			// Rethrow so the caller does NOT navigate away from a step whose save was rejected —
			// swallowing here used to lose the typed content (the unmount dropped it silently).
			throw error;
		}
	};

	// Rejects when the save fails: the displayed step only changes on success, and callers must
	// not navigate (nor move the menu highlight) on failure — swallowing here used to leave the
	// menu on the target step while the page stayed on the current one, with no feedback at all.
	const saveOnboardingFormAsync = async (newStep: FreelancerOnboardingFormStep) => {
		if (newStep > currentOnboardingStep) {
			await validateAndSaveOnboardingFormAsync(currentOnboardingStep, newStep);
		} else {
			await saveOnboardingFormWithoutValidationAsync(currentOnboardingStep, newStep);
		}

		setCurrentOnboardingFormStep(newStep);
	};

	const saveOnboardingFormStepAsync = async (newStep: FreelancerOnboardingFormStep) => {
		setIsSaveProgressPending(true);

		try {
			await updateOnboardingFormsStep.mutateAsync({
				freelancerOnboardingId: freelancerProfile!.onboarding.id,
				freelancerOnboardingFormsProgressId:
					freelancerProfile!.onboarding.formsProgress.id,
				newStep
			});

			switch (currentOnboardingStep) {
				case FreelancerOnboardingFormStep.MyExpertise:
					setFreelancerOnboardingMyExpertiseStepViewModel(null);
					break;
				case FreelancerOnboardingFormStep.MySkills:
					setFreelancerOnboardingMySkillsStepViewModel(null);
					break;
				case FreelancerOnboardingFormStep.MySectors:
					setFreelancerOnboardingMySectorsStepViewModel(null);
					break;
				case FreelancerOnboardingFormStep.MyPresentation:
					setFreelancerOnboardingMyPresentationStepViewModel(null);
					break;
				case FreelancerOnboardingFormStep.MyAvailability:
					setFreelancerOnboardingMyAvailabilityStepViewModel(null);
					break;
				case FreelancerOnboardingFormStep.MyProfessionalInformation:
					setFreelancerOnboardingMyProfessionalInformationStepViewModel(null);
					break;
				case FreelancerOnboardingFormStep.MyPersonalInformation:
					setFreelancerOnboardingMyPersonalInformationStepViewModel(null);
					break;
			}

			setCurrentOnboardingFormStep(newStep);
			setIsSaveProgressPending(false);
		} catch (error) {
			console.error("Error saving onboarding:", error);
			setIsSaveProgressPending(false);
			// Rethrow so the caller does NOT treat a rejected save as a successful step change.
			throw error;
		}
	};

	return {
		validateAndSaveOnboardingFormAsync,
		saveOnboardingFormWithoutValidationAsync,
		saveOnboardingFormAsync,
		saveOnboardingFormStepAsync,
		isSaveProgressPending
	};
};
